import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { shipmentsApi, type CreateShipmentPayload } from '@/api/shipmentsApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Ship products to a partner (z8nrz7eaex). On success every product list and
 * every storage view is refreshed: the products have left their slots and now
 * show the partner's code as their location.
 */
export function useCreateShipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateShipmentPayload) => shipmentsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      void qc.invalidateQueries({ queryKey: ['storage-nodes'] })
      void qc.invalidateQueries({ queryKey: ['procedure-board'] })
      void qc.invalidateQueries({ queryKey: ['shipments'] })
    },
  })
}

/**
 * The shipments a product has been on, newest first — how an already-shipped
 * product's truck icon finds the shipment whose label to reprint.
 */
export function useProductShipments(productId: string | null, enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['shipments', 'by-product', selectedTenantId, productId],
    enabled: enabled && !!productId && !!selectedTenantId,
    queryFn: () => shipmentsApi.list(productId as string),
  })
}
